import { askAboutAndReveal } from "../../../state/chatActions";
import type { MerchantSummary, MonthlyRow } from "../../../data/types";
import { MCC_RAMP } from "../../../data/seasonality";
import { fmtPct } from "./format";
import styles from "./ProjectionChart.module.css";

interface ProjectionChartProps {
  merchant: MerchantSummary;
  monthly: MonthlyRow[];
  /** If set, render a dashed "managed" line assuming this efficacy applied now. */
  managedEfficacy?: number;
}

export function ProjectionChart({
  merchant,
  monthly,
  managedEfficacy = 0.45,
}: ProjectionChartProps) {
  const history = monthly.map((r) => ({ t: r.month, v: r.vampRatio }));
  const ramp = MCC_RAMP[merchant.mcc] ?? 0.02;
  const lastV = history[history.length - 1]?.v ?? merchant.fraudRatioPct;

  const forwardMonths = 12;
  const noAction: { t: string; v: number }[] = [];
  const managed: { t: string; v: number }[] = [];
  for (let i = 1; i <= forwardMonths; i++) {
    noAction.push({ t: `+${i}`, v: lastV + ramp * i });
    managed.push({ t: `+${i}`, v: lastV + ramp * i * (1 - managedEfficacy) });
  }

  const all = [...history, ...noAction.map((p, i) => ({ t: p.t, v: p.v, proj: true, idx: history.length + i }))];
  const width = 520;
  const height = 140;
  const pad = 16;
  const maxY = Math.max(1.7, ...all.map((p) => p.v));
  const totalLen = history.length + forwardMonths;
  const toX = (i: number) =>
    pad + (i / Math.max(1, totalLen - 1)) * (width - pad * 2);
  const toY = (v: number) => height - pad - (v / maxY) * (height - pad * 2);

  const historyPath = history
    .map((p, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)} ${toY(p.v).toFixed(1)}`)
    .join(" ");
  const noActionPath = noAction
    .map((p, i) => {
      const xi = history.length + i;
      const prevXi = i === 0 ? history.length - 1 : xi;
      const prevV = i === 0 ? lastV : noAction[i - 1].v;
      if (i === 0) {
        return `M${toX(prevXi).toFixed(1)} ${toY(prevV).toFixed(1)} L${toX(xi).toFixed(1)} ${toY(p.v).toFixed(1)}`;
      }
      return `L${toX(xi).toFixed(1)} ${toY(p.v).toFixed(1)}`;
    })
    .join(" ");
  const managedPath = managed
    .map((p, i) => {
      const xi = history.length + i;
      if (i === 0) {
        return `M${toX(xi - 1).toFixed(1)} ${toY(lastV).toFixed(1)} L${toX(xi).toFixed(1)} ${toY(p.v).toFixed(1)}`;
      }
      return `L${toX(xi).toFixed(1)} ${toY(p.v).toFixed(1)}`;
    })
    .join(" ");

  const breachMoNoAction = noAction.find((p) => p.v >= 1.5);
  const breachMoManaged = managed.find((p) => p.v >= 1.5);

  return (
    <div className={styles.wrap}>
      <header className={styles.header}>
        <span className={styles.title}>breach projection</span>
        <span className={styles.muted}>18-month forward</span>
      </header>
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="none" role="img" aria-label="vamp projection chart">
        <line x1={pad} x2={width - pad} y1={toY(1.5)} y2={toY(1.5)} className={styles.critical} />
        <line x1={pad} x2={width - pad} y1={toY(0.9)} y2={toY(0.9)} className={styles.warn} />
        {history.length > 0 && (
          <line
            x1={toX(history.length - 1)}
            x2={toX(history.length - 1)}
            y1={pad}
            y2={height - pad}
            className={styles.nowMarker}
          />
        )}
        <path d={historyPath} className={styles.lineHistory} />
        <path d={noActionPath} className={styles.lineNoAction} />
        <path d={managedPath} className={styles.lineManaged} />
      </svg>
      <footer className={styles.legend}>
        <button
          type="button"
          className={styles.legendItem}
          onClick={() =>
            askAboutAndReveal(
              `summarise ${merchant.name}'s vamp history — where it was 12 months ago, where it peaked, and the shape of the trend over the last 6 months.`
            )
          }
        >
          <span className={`${styles.dot} ${styles.dotHistory}`} /> history
        </button>
        <button
          type="button"
          className={styles.legendItem}
          onClick={() =>
            askAboutAndReveal(
              `if we don't intervene on ${merchant.name}, ${breachMoNoAction ? `we breach in ${noAction.indexOf(breachMoNoAction) + 1}mo` : "the ratio stays below 1.5%"}. walk me through what drives that no-action projection and whether the mcc ramp is realistic for this merchant.`
            )
          }
        >
          <span className={`${styles.dot} ${styles.dotNoAction}`} /> no action →{" "}
          {breachMoNoAction
            ? `breach in ${noAction.indexOf(breachMoNoAction) + 1}mo`
            : "stable"}
        </button>
        <button
          type="button"
          className={styles.legendItem}
          onClick={() =>
            askAboutAndReveal(
              `under the managed trajectory, ${merchant.name} ${breachMoManaged ? `still breaches in ${managed.indexOf(breachMoManaged) + 1}mo` : `stays below ${fmtPct(1.5, 1)}`}. what interventions are baked into "managed" here and what efficacy assumption makes this curve?`
            )
          }
        >
          <span className={`${styles.dot} ${styles.dotManaged}`} /> managed →{" "}
          {breachMoManaged
            ? `breach in ${managed.indexOf(breachMoManaged) + 1}mo`
            : `≤ ${fmtPct(1.5, 1)}`}
        </button>
      </footer>
    </div>
  );
}
